import { matchPath, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Container, Icon, Tooltip } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import {
  ArchivedDriversActionMenu,
  DriversFilters,
  DriversFiltersProvider,
  DriversModals,
  DriversContextProvider,
  MyDriversActionMenu,
  OffloadDriversActionMenu,
  PageHeader,
} from "@/components/organisms";
import { useOperator, useSubscriptionAssignment } from "@/hooks";
import { useGetFleetDriversQuery } from "@/redux/apis/driver/driverApi";
import { getPlanLimits, shouldShowLimitIndicator } from "@/redux/apis/subscription/subscriptionLimits";
import { useAppSelector } from "@/redux/hooks";
import { fleetDriversPageSelector } from "@/redux/slices/driver/selectors";
import { ApplicationRules } from "./ApplicationRules";
import { ArchivedDrivers } from "./ArchivedDrivers";
import { MyDrivers } from "./MyDrivers";
import { OffloadDrivers } from "./OffloadDrivers";

export const Drivers = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const { subscriptionStatus } = useSubscriptionAssignment();

  // Get current driver count for My Drivers tab
  useGetFleetDriversQuery(
    {
      provider_uuid: operatorId ?? "",
      page: "1",
      page_size: "1",
    },
    {
      skip: !operatorId,
    }
  );

  const planSlug = subscriptionStatus.baseSubscription?.subscription.name.toLowerCase() ?? "";
  const limits = getPlanLimits(planSlug);
  const showLimitIndicator = shouldShowLimitIndicator(planSlug);
  const driverCount = useAppSelector(fleetDriversPageSelector).total ?? 0;

  const isAtDriverLimit = driverCount >= limits.maxDrivers;
  const remainingDrivers = limits.maxDrivers - driverCount;

  const handleUpgradeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/operator/${operatorId}/payments/subscriptions/subscription-plan`);
  };

  const driverTooltipContent = isAtDriverLimit
    ? `You've reached the maximum of ${limits.maxDrivers} drivers for your current plan. Remove an existing driver or `
    : `This plan allows up to ${limits.maxDrivers} drivers. You can add ${remainingDrivers} more.`;

  const driverTooltipIcon = showLimitIndicator ? (
    <Tooltip
      content={
        isAtDriverLimit ? (
          <span>
            You&apos;ve reached the maximum of {limits.maxDrivers} drivers for your current plan. Remove an existing driver or{" "}
            <a href="#" className="text-info underline" onClick={handleUpgradeClick}>
              upgrade
            </a>{" "}
            your plan to add more.
          </span>
        ) : (
          driverTooltipContent
        )
      }
      placement="top-end"
      interactive={isAtDriverLimit}
      interactiveBorder={20}
      interactiveDebounce={0}
      appendTo={() => document.body}
    >
      <Icon name="InfoCircle" size="md" className="ml-2 rotate-180 text-neutral-silver-gray" variant="Bold" />
    </Tooltip>
  ) : undefined;

  const pageTabs = [
    {
      name: showLimitIndicator ? `My Drivers (${driverCount}/${limits.maxDrivers})` : "My Drivers",
      to: "my-drivers",
      icon: driverTooltipIcon,
    },
    {
      name: "Offload Drivers",
      to: "offload",
    },
    {
      name: "Archived Drivers",
      to: "archived",
    },
  ];

  const isParentPage = Boolean(
    matchPath({ path: "/drivers/:slug" }, pathname) || matchPath({ path: "operator/:operatorId/drivers/:slug" }, pathname)
  );

  return (
    <DriversContextProvider>
      <DriversFiltersProvider>
        <Container className="w-full">
          <div className="sticky top-0 z-30 w-full bg-white">
            <PageHeader title="Drivers" skipCrumbs={1} />
            {isParentPage && (
              <div className="flex w-full gap-2 border-b !border-neutral-mid-gray py-1 max-xl:justify-between lg:py-0">
                <Tabs className="no-scrollbar min-w-0 border-none" tabs={pageTabs} />
                <ActionMenu />
              </div>
            )}
            <DriversFilters.ActiveFilters className="mt-2" />
          </div>
          <Outlet />
        </Container>

        <DriversModals />
      </DriversFiltersProvider>
    </DriversContextProvider>
  );
};

const ActionMenu = () => {
  const { pathname } = useLocation();

  switch (true) {
    case pathname.includes("/drivers/my-drivers"):
      return <MyDriversActionMenu />;
    case pathname.includes("/drivers/offload"):
      return <OffloadDriversActionMenu />;
    case pathname.includes("/drivers/archived"):
      return <ArchivedDriversActionMenu />;
    default:
      return null;
  }
};

Drivers.MyDrivers = MyDrivers;
Drivers.OffloadDrivers = OffloadDrivers;
Drivers.ArchivedDrivers = ArchivedDrivers;
Drivers.ApplicationRules = ApplicationRules;
